'use client'

import CardOrder from '@/entities/profile-page/historyOrders/CardOrder'
import { useInView } from 'react-intersection-observer'
import { useGetIsAuthUser } from '@/shared/hooks/useGetIsAuthUser.js'
import { useGetFilteredOrdersMutation, useGetOrderStatusesMutation } from '@/shared/api-rtk/index.js'
import { useEffect, useState } from 'react'

export const HistoryOrders = () => {
    const { ref, inView } = useInView({
        threshold: 0,
        triggerOnce: true,
    })
    const { userInfo } = useGetIsAuthUser()
    const [getFilteredOrders] = useGetFilteredOrdersMutation()
    const [getOrderStatuses] = useGetOrderStatusesMutation()
    
    const [orders, setOrders] = useState([])
    const [loading, setLoading] = useState(false)
    const [statuses, setStatuses] = useState({
        order: [],
        delivery: [],
        payment: []
    })
    
    const [filters, setFilters] = useState({
        customer_id: userInfo?.id || null,
        filter_status_order: null,
        filter_status_delivery: null,
        filter_status_payment: null,
        limit: 100
    })

    // Загрузка статусов
    useEffect(() => {
        loadStatuses()
    }, [])

    // Загрузка заказов при изменении фильтров
    useEffect(() => {
        if (userInfo?.id) {
            loadOrders()
        }
    }, [filters, userInfo])

    const loadStatuses = async () => {
        try {
            const result = await getOrderStatuses().unwrap()
            if (result.data) {
                setStatuses({
                    order: result.data.order_statuses || [],
                    delivery: result.data.delivery_statuses || [],
                    payment: result.data.payment_statuses || []
                })
            }
        } catch (error) {
            console.error('Ошибка загрузки статусов:', error)
        }
    }

    const loadOrders = async () => {
        if (!userInfo?.id) return
        
        setLoading(true)
        try {
            const result = await getFilteredOrders({
                ...filters,
                customer_id: userInfo.id
            }).unwrap()
            
            if (result.data) {
                setOrders(Object.values(result.data))
            } else {
                setOrders([])
            }
        } catch (error) {
            console.error('Ошибка загрузки заказов:', error)
            setOrders([])
        } finally {
            setLoading(false)
        }
    }

    const handleFilterChange = (filterType, value) => {
        setFilters(prev => ({
            ...prev,
            [filterType]: value || null
        }))
    }

    const resetFilters = () => {
        setFilters({
            customer_id: userInfo?.id || null,
            filter_status_order: null,
            filter_status_delivery: null,
            filter_status_payment: null,
            limit: 100
        })
    }

    // Обработчик обновления заказа после отмены
    const handleOrderUpdated = (orderId) => {
        // Перезагружаем список заказов
        loadOrders()
    }

    return (
        <div
            ref={ref}
            className={`HistoryOrders ${
                inView && 'animate__animated animate__zoomInRight'
            }`}
        >
            <h2>Мои заказы</h2>
            
            {/* Фильтры */}
            <div className="orders-filters">
                <div className="filter-group">
                    <label>Статус заказа</label>
                    <select 
                        className="filter-select"
                        onChange={(e) => handleFilterChange('filter_status_order', e.target.value)}
                        value={filters.filter_status_order || ''}
                    >
                        <option value="">Все</option>
                        {statuses.order.map(status => (
                            <option key={status.order_new_status_id} value={status.number}>
                                {status.name}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="filter-group">
                    <label>Статус доставки</label>
                    <select 
                        className="filter-select"
                        onChange={(e) => handleFilterChange('filter_status_delivery', e.target.value)}
                        value={filters.filter_status_delivery || ''}
                    >
                        <option value="">Все</option>
                        {statuses.delivery.map(status => (
                            <option key={status.order_new_status_id} value={status.number}>
                                {status.name}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="filter-group">
                    <label>Статус оплаты</label>
                    <select 
                        className="filter-select"
                        onChange={(e) => handleFilterChange('filter_status_payment', e.target.value)}
                        value={filters.filter_status_payment || ''}
                    >
                        <option value="">Все</option>
                        {statuses.payment.map(status => (
                            <option key={status.order_new_status_id} value={status.number}>
                                {status.name}
                            </option>
                        ))}
                    </select>
                </div>

                {(filters.filter_status_order || filters.filter_status_delivery || filters.filter_status_payment) && (
                    <button onClick={resetFilters} className="reset-filters-btn">
                        Сбросить
                    </button>
                )}
            </div>

            {/* Список заказов */}
            <div className={'history-content'}>
                {loading && <div className="loading-orders">Загрузка...</div>}
                {!loading && !orders.length && <h3>Нет заказов</h3>}
                {!loading && orders.map((order) => (
                    <CardOrder 
                        key={order.order_id} 
                        orderData={order} 
                        onOrderUpdated={handleOrderUpdated}
                    />
                ))}
            </div>
        </div>
    )
}